// Code-based TanStack Router. We avoid the file-based plugin so the build is
// just `vite` with no codegen step. Routes mirror the journeys in the spec:
// landing → magic link → callback → student or creator dashboards.

import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
} from '@tanstack/react-router';
import { Toaster } from 'sonner';
import { AmbientBackground } from './components/ambient-background.tsx';
import { BottomNav } from './components/bottom-nav.tsx';
import { RouteError } from './components/route-error.tsx';
import { SidebarNav } from './components/sidebar-nav.tsx';
import { TopbarMinimal } from './components/topbar.tsx';
import { useSession } from './lib/session.tsx';

const TOAST_STYLE = {
  fontFamily: 'var(--font-body)',
  background: 'var(--color-bg-card)',
  color: 'var(--color-fg)',
  border: '1px solid var(--color-border)',
} as const;

// Eager: landing + callback are the critical entry path
import { CallbackRoute } from './routes/callback.tsx';
import { LandingRoute } from './routes/landing.tsx';
import { NotFoundRoute } from './routes/not-found.tsx';

function RootLayout() {
  const { user } = useSession();
  const authed = Boolean(user);
  return (
    <div className="min-h-screen relative">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:rounded-lg focus:bg-accent focus:text-white focus:px-4 focus:py-2 focus:text-[14px] focus:font-heading focus:font-bold"
      >
        Перейти к содержимому
      </a>
      {authed ? <AmbientBackground /> : null}
      {authed ? <TopbarMinimal /> : null}
      <SidebarNav />
      <div id="main-content" className={authed ? 'xl:pl-60 pb-20 xl:pb-0' : ''}>
        <Outlet />
      </div>
      <BottomNav />
      <Toaster position="top-center" toastOptions={{ style: TOAST_STYLE }} />
    </div>
  );
}

const rootRoute = createRootRoute({
  notFoundComponent: NotFoundRoute,
  errorComponent: RouteError,
  component: RootLayout,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: LandingRoute,
});

const callbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/callback',
  component: CallbackRoute,
});

// Lazy-loaded routes — split into separate chunks
const studentDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/student',
  component: lazyRouteComponent(
    () => import('./routes/student-dashboard.tsx'),
    'StudentDashboardRoute',
  ),
});

const studentCourseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/student/courses/$courseId',
  component: lazyRouteComponent(() => import('./routes/student-course.tsx'), 'StudentCourseRoute'),
});

const studentLessonRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/student/lessons/$lessonId',
  component: lazyRouteComponent(() => import('./routes/student-lesson.tsx'), 'StudentLessonRoute'),
});

const creatorDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/creator',
  component: lazyRouteComponent(
    () => import('./routes/creator-dashboard.tsx'),
    'CreatorDashboardRoute',
  ),
});

const creatorCourseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/creator/courses/$courseId',
  component: lazyRouteComponent(() => import('./routes/creator-course.tsx'), 'CreatorCourseRoute'),
});

const studentProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/student/profile',
  component: lazyRouteComponent(
    () => import('./routes/student-profile.tsx'),
    'StudentProfileRoute',
  ),
});

const studentVocabRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/student/vocab',
  component: lazyRouteComponent(() => import('./routes/student-vocab.tsx'), 'StudentVocabRoute'),
});

const publicCourseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/c/$slug',
  component: lazyRouteComponent(() => import('./routes/public-course.tsx'), 'PublicCourseRoute'),
});

const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite/$token',
  component: lazyRouteComponent(() => import('./routes/invite.tsx'), 'InviteRedeemRoute'),
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  callbackRoute,
  publicCourseRoute,
  studentDashboardRoute,
  studentCourseRoute,
  studentLessonRoute,
  studentProfileRoute,
  studentVocabRoute,
  creatorDashboardRoute,
  creatorCourseRoute,
  inviteRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultViewTransition: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
